import { useState } from 'react'
import type { LegJson, QuoteResponse, RouteJson } from '../api/types'
import { formatAmount, formatBps, formatMs, shortAddress } from '../lib/format'

/**
 * What the engine actually decided, rendered.
 *
 * This is the part of WP6 the brief asks for by name — "show the split breakdown" — and it
 * is worth more than decoration. Splitting wins on roughly a third of realistic (pair, size)
 * combinations by a median of 4 bps, and the only way anyone can tell whether that is still
 * true in production is if the allocation, the reason and the gas adjustment are on screen
 * next to the number the user is about to accept.
 *
 * `split.reason` is rendered as the enum the API sends rather than flattened into prose, so
 * "we did not split because gas made it not worth it" stays distinguishable from "we could
 * not price gas at all". The second one is a degraded quote wearing the first one's clothes.
 */

const PROTOCOL_LABEL: Record<string, string> = {
  v2: 'Uniswap V2',
  v3: 'Uniswap V3',
  algebra: 'Algebra V4',
}

function protocolLabel(protocol: string): string {
  return PROTOCOL_LABEL[protocol] ?? protocol
}

function RoutePath({ route }: { route: RouteJson }) {
  return (
    <div className="route-path">
      {route.path.map((token, index) => (
        <span key={`${token.address}-${index}`} className="route-node">
          {index > 0 && <span className="route-arrow">→</span>}
          <span title={token.address}>{token.symbol ?? shortAddress(token.address)}</span>
        </span>
      ))}
    </div>
  )
}

function Leg({ leg, decimalsOut, symbolOut }: { leg: LegJson; decimalsOut: number; symbolOut: string }) {
  return (
    <li className="leg">
      <div className="leg-head">
        <span className={`tag tag-${leg.protocol}`}>{protocolLabel(leg.protocol)}</span>
        <span className="leg-percent">{leg.percent}%</span>
      </div>
      <RoutePath route={leg.route} />
      <div className="leg-foot">
        <span>
          {formatAmount(leg.amountOut, decimalsOut)} {symbolOut}
        </span>
        <span
          className="muted"
          title="Gas the split search charged this leg: the quoter's estimate plus WP4's measured correction."
        >
          {Number(leg.gasUnits).toLocaleString('en-US')} gas
        </span>
      </div>
      <div className="leg-bar">
        <div className="leg-bar-fill" style={{ width: `${leg.percent}%` }} />
      </div>
    </li>
  )
}

export function RouteBreakdown({ quote }: { quote: QuoteResponse }) {
  const [showEngine, setShowEngine] = useState(false)
  const { split, allocation, tokenOut } = quote
  const symbolOut = tokenOut.symbol ?? 'out'

  return (
    <section className="breakdown">
      <header className="breakdown-head">
        <h3>{split.chosen === 'split' ? `Split across ${allocation.length} routes` : 'Single best route'}</h3>
        {split.improvementBps !== null && split.chosen === 'split' && (
          <span className="pill pill-good" title="Gas-adjusted improvement over the best single route.">
            +{split.improvementBps.toFixed(1)} bps vs. single
          </span>
        )}
      </header>

      <p className="breakdown-reason">
        <code>{split.reason}</code> {split.detail}
      </p>

      <ul className="legs">
        {allocation.map((leg, index) => (
          <Leg key={`${leg.route.id}-${index}`} leg={leg} decimalsOut={tokenOut.decimals} symbolOut={symbolOut} />
        ))}
      </ul>

      <dl className="facts">
        <div>
          <dt title="Measured against the quoter's own curve, not against any local price.">Price impact</dt>
          <dd>
            {quote.priceImpact && !quote.priceImpact.unavailableReason ? (
              formatBps(quote.priceImpact.bps)
            ) : (
              <span className="muted" title={quote.priceImpact?.unavailableReason ?? ''}>
                not measurable
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt>Gas</dt>
          <dd>
            {Number(quote.gas.units).toLocaleString('en-US')}
            {quote.gas.costInTokenOut && (
              <span className="muted">
                {' '}
                ≈ {formatAmount(quote.gas.costInTokenOut, tokenOut.decimals)} {symbolOut}
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt>Quoted at block</dt>
          <dd>{quote.blockNumber ?? '—'}</dd>
        </div>
      </dl>

      {quote.gas.costInTokenOut === null && (
        <p className="hint">
          Gas could not be priced in {symbolOut}, so the engine fell back to the single best route rather than comparing
          split plans against a gas cost of zero.
        </p>
      )}

      <button type="button" className="btn btn-ghost btn-small" onClick={() => setShowEngine((open) => !open)}>
        {showEngine ? 'Hide' : 'Show'} engine detail
      </button>

      {showEngine && (
        <dl className="facts facts-dense">
          <div>
            <dt>Routes enumerated / quoted</dt>
            <dd>
              {quote.stats.routesEnumerated} / {quote.stats.routesQuoted}
            </dd>
          </div>
          <div>
            <dt title="Simulated swaps through OmegaQuoter. The binding constraint on this chain.">Quoter calls</dt>
            <dd>
              {quote.stats.quoterCalls} in {quote.stats.multicalls} multicalls
            </dd>
          </div>
          <div>
            <dt>Split search</dt>
            <dd>
              {split.algorithm}, {split.candidatesEvaluated.toLocaleString('en-US')} allocations over{' '}
              {split.routesConsidered} routes in {formatMs(split.searchMs)}
            </dd>
          </div>
          <div>
            <dt>Phases</dt>
            <dd>
              candidates {formatMs(quote.timings.candidatesMs)} · enumerate {formatMs(quote.timings.enumerationMs)} ·
              quote {formatMs(quote.timings.quotingMs)} · split {formatMs(quote.timings.splitMs)}
            </dd>
          </div>
          <div>
            <dt>Total</dt>
            <dd>
              {formatMs(quote.timings.totalMs)}
              {quote.cache.hit && <span className="muted"> · served from cache, {quote.cache.ageMs} ms old</span>}
            </dd>
          </div>
          {split.baseline && (
            <div>
              <dt>Single-route baseline</dt>
              <dd>
                {formatAmount(split.baseline.amountOut, tokenOut.decimals)} {symbolOut}
                {split.baseline.route && (
                  <span className="muted"> via {protocolLabel(split.baseline.route.protocol)}</span>
                )}
              </dd>
            </div>
          )}
        </dl>
      )}
    </section>
  )
}
